<template>
  <v-card class="messages-container fill-height">
    <v-card-text class="messages" id="messages" @scroll="onScroll" ref="messages">

      <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
      ></v-progress-linear>

      <v-list ref="messagesList">

        <v-list-item v-if="!messages.length && !loading" class="d-flex justify-center">
          <span class="text-h6">No messages loaded</span>
        </v-list-item>

        <div v-for="(msg, idx) in groupedMessages" :key="idx">
          <div v-if="msg.marker || msg.type==='marker'" id="marker" ref="marker">
            <span v-if="displayID">
              <p style="text-align: center" class="mb-0">
                <b style="font-size: 1.2em; font-weight: 900">
                  <v-icon small v-if="hasPastNames">mdi-star</v-icon>
                  {{ displayID }}
                </b>
                <v-btn small icon @click="openPopup">
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-btn>
              </p>
            </span>
          </div>
          <v-list-item v-else>
            <v-list-item-content>
              <template v-if="msg.type==='handend'">
                <v-list-item-title style="color: deepskyblue">
                  {{ getTime(msg.ts) }} -
                  <template v-if="msg.end > msg.hand_num">
                    <a @click="loadPastHand(msg)">Hand #{{ msg.hand_num }} - {{msg.end}} Finished</a>
                    <div v-if="msg.text" class="handend-div">
                      <p v-for="(txt, idx) of msg.text.split('\n')" :key="idx" :class="[getHandendClass(txt)]">{{ getParsedHandendText(txt) }}</p>
                    </div>
                  </template>
                  <template v-else>
                    <a @click="loadPastHand(msg)">Hand #{{ msg.hand_num }} Finished</a>
                    <div v-if="msg.text" class="handend-div">
                      <p v-for="(txt, idx) of msg.text.split('\n')" :key="idx" :class="[getHandendClass(txt)]">{{ getParsedHandendText(txt) }}</p>
                    </div>
                  </template>
                </v-list-item-title>
              </template>
              <template v-else-if="msg.type==='suggestion'">
                <div class="chat-suggestion">
                  <span class="mr-2">{{ getTime(msg.ts) }}</span>
                  <span>Suggested: {{ msg.text }}</span>
                </div>
              </template>
              <template v-else-if="msg.type==='game_event'">
                <v-list-item-title style="color: springgreen">
                  <span>{{ getTime(msg.ts) }} - <v-icon small color="springgreen">mdi-alert-circle</v-icon> {{ msg.text }}</span>
                </v-list-item-title>
              </template>
              <template v-else-if="msg.type==='warning'">
                <v-list-item-title style="color: orange">
                  <span>{{ getTime(msg.ts) }} - <v-icon small color="orange">mdi-alert</v-icon> {{ msg.text }}</span>
                </v-list-item-title>
              </template>
              <template v-else-if="msg.type==='room'">
                <div class="chat-room">
                  <span class="mr-2">{{ getTime(msg.ts) }}</span>
                  <template v-if="msg.room_id">
                    <span>Messages for room: {{ msg.room_id }}</span>
                    &nbsp;&nbsp;
                    <span>
                      <template v-if="rooms[msg.room_id] && rooms[msg.room_id].chat_analytics">
                        cph/size:
                          <b>{{
                            formatNumber(rooms[msg.room_id].chat_analytics.all_time.avg_chats_per_hand, 1)
                          }}/{{
                            formatNumber(rooms[msg.room_id].chat_analytics.all_time.avg_chat_length, 1)
                          }}</b>
                        all time
                      </template>
                      <template v-else-if="rooms[msg.room_id]">
                        chat analytics never calculated
                      </template>
                      <template v-else>
                        room info never sent
                      </template>
                    </span>
                  </template>
                  <template v-else><span>lobby</span></template>
                </div>
              </template>

              <template v-else-if="msg.type==='gift_chat'">
                <v-list-item-title class="d-flex align-center justify-center">
                  <span>{{ getTime(msg.ts) }} - <v-icon color="primary">mdi-gift</v-icon> <strong style="font-size: 1.1em">{{ msg.name }}</strong> sent a </span>
                  <img style="width: 5em" :src="'/static/imgs/gifts/' + msg.giftId + '.png'"/>
                  <span>({{ msg.giftId }}) to <strong style="font-size: 1.1em"><span :style="msg.targetIsBot ? 'color: limegreen' : ''">{{ msg.targetName }}</span></strong></span>
                </v-list-item-title>
              </template>

              <template v-else-if="msg.type==='emoji_chat'">
                <v-list-item-title class="d-flex align-center justify-center">
                  <span>{{ getTime(msg.ts) }} - <v-icon color="primary">mdi-emoticon</v-icon> <strong style="font-size: 1.1em">{{ msg.name }}</strong> sent </span>
                  <img style="width: 5em; margin: 0 10px;" :src="'/static/imgs/emojis/' + getEmojiImage(msg.emojiId) + '.png'"/>
                  <span>({{ msg.text }})</span>
                </v-list-item-title>
              </template>

              <template v-else-if="msg.type==='voice_chat'">
                <v-list-item-title>{{ getTime(msg.ts) }} - <span :style="getSenderTitleStyle(msg)">{{ msg.name }}</span> ( <span v-if="club.currency==='USD'">$</span>{{ msg.result }} )</v-list-item-title>
                <v-list-item-subtitle class="mt-2 messages-list" :style="getSenderTitleStyle(msg)">
                  <template v-if="!msg.filename"><v-chip small>AUDIO MESSAGE</v-chip> Audio not found</template>
                  <template v-else>
                    <audio-player style="max-width: 350px" :file="'/voice/' + msg.filename"></audio-player>
                  </template>
                </v-list-item-subtitle>
              </template>

              <template v-else-if="msg.type==='announcement_chat'">

                <v-list-item-title class="d-flex align-center justify-center">
                  <span>{{ getTime(msg.ts) }} - <v-icon color="yellow">mdi-bullhorn</v-icon> <strong style="font-size: 1.1em">{{ msg.name }}</strong> sent an annoucement</span>
                </v-list-item-title>

                <v-list-item-subtitle class="mt-2 messages-list d-flex flex-column align-center justify-center" :style="getSenderTitleStyle(msg)">
                  <p style="white-space: break-spaces;" class="yellow--text text-body-1 font-weight-bold">
                    <template v-if="msg.name_mentions && msg.name_mentions.length">
                      <span>{{ msg.text.substr(0, msg.text.indexOf(msg.name_mentions[0].match_str))
                      }}</span><span class="mention-highlight">{{
                      msg.text.substring(msg.text.indexOf(msg.name_mentions[0].match_str), msg.text.indexOf(msg.name_mentions[0].match_str) + msg.name_mentions[0].match_str.length)
                      }}</span><span>{{ msg.text.substring(msg.text.indexOf(msg.name_mentions[0].match_str) + msg.name_mentions[0].match_str.length, msg.text.length) }}</span>
                    </template>
                    <span v-else>{{ msg.text }}</span>
                  </p>
                </v-list-item-subtitle>

              </template>

              <template v-else>
                <template v-if="isSenderActiveAccount(msg)">
                  <v-list-item-title style="text-align: right">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on" style="cursor: pointer;">
                          {{ msg.name }} ( <span v-if="club.currency==='USD'">$</span>{{ msg.result }} ) - {{ getTime(msg.ts) }}
                        </span>
                      </template>
                      <span>Sent by <strong class="text-h6 font-weight-bold">{{ msg.senderName }}</strong></span>
                    </v-tooltip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="mt-2 messages-list">
                    <p v-for="(line, i) of msg.lines" :key="i" style="text-align: right; white-space: break-spaces;">
                      <v-row class="btn-add">
                        <v-col cols="12" class="d-flex align-center justify-end">
                          <span>{{ line.text }}</span>
                        </v-col>
                      </v-row>
                    </p>
                  </v-list-item-subtitle>
                </template>
                <template v-else-if="isSenderOtherBot(msg)">
                  <v-list-item-title style="text-align: center">
                    {{ msg.name }} ( <span v-if="club.currency==='USD'">$</span>{{ msg.result }} ) - {{ getTime(msg.ts) }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="mt-2 messages-list">
                    <p v-for="(line, i) of msg.lines" :key="i" style="text-align: center; white-space: break-spaces;">
                      <v-row class="btn-add">
                        <v-col cols="12" class="d-flex align-center justify-center">
                          <span>{{ line.text }}</span>
                        </v-col>
                      </v-row>
                    </p>
                  </v-list-item-subtitle>
                </template>
                <template v-else>
                  <v-list-item-title>{{ getTime(msg.ts) }} - <span :style="getSenderTitleStyle(msg)">{{ msg.name }}</span> ( <span v-if="club.currency==='USD'">$</span>{{ msg.result }} )</v-list-item-title>
                  <v-list-item-subtitle class="mt-2 messages-list" :style="getSenderTitleStyle(msg)">
                    <p v-for="(line, i) of msg.lines" :key="i" style="white-space: break-spaces;">
                      <v-row class="btn-add">
                        <v-col cols="8" class="d-flex align-center">
                          <template v-if="line.name_mentions && line.name_mentions.length">
<span>{{ line.text.substr(0, line.text.indexOf(line.name_mentions[0].match_str))
  }}</span><span class="mention-highlight">{{
  line.text.substring(line.text.indexOf(line.name_mentions[0].match_str), line.text.indexOf(line.name_mentions[0].match_str) + line.name_mentions[0].match_str.length)
}}</span><span>{{ line.text.substring(line.text.indexOf(line.name_mentions[0].match_str) + line.name_mentions[0].match_str.length, line.text.length) }}</span>
                          </template>

                          <span v-else>{{ line.text }}</span>
                        </v-col>
                        <v-col cols="4" class="d-flex align-center justify-end">
                          <v-btn small v-if="addSuggestion && !isOurs(msg.name)" @click="add(line.text)" color="primary">
                            <v-icon>mdi-plus</v-icon>
                            Add to
                          </v-btn>
                        </v-col>
                      </v-row>
                    </p>
                  </v-list-item-subtitle>
                </template>
              </template>
            </v-list-item-content>
          </v-list-item>
        </div>
      </v-list>
    </v-card-text>

    <v-dialog v-model="askAdd" width="500">
      <v-card>
        <v-card-title>Add Suggestion {{ suggestionData.isTrigger ? 'Trigger' : '' }}</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-select
                  :disabled="isShowdown"
                  :items="suggestionTypes"
                  v-model="suggestionData.type"
                  outlined
                  hide-details
                  label="Type"
              >
                <template v-slot:selection="{ item }">
                  <span>
                    {{ item.text }}<span style="color: gray"> - {{ item.description }}</span>
                  </span>
                </template>
                <template v-slot:item="{ item }">
                  <span>
                    {{ item.text }}<span style="color: gray"> - {{ item.description }}</span>
                  </span>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-select
                  :items="suggestionGameTypes"
                  v-model="suggestionData.gameType"
                  outlined
                  hide-details
                  multiple
                  label="Game Type"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="!isShowdown">
            <v-col cols="12" class="d-flex justify-center align-center">
              <v-checkbox
                label="Is Trigger?"
                v-model="suggestionData.isTrigger"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field
                outlined
                hide-details
                label="Text"
                v-model="suggestionData.text"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="askAdd=false">Cancel</v-btn>
          <v-btn color="green" text @click="addAction(); askAdd=false">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {mapGetters} from "vuex";
import AudioPlayer from './chat/AudioPlayer.vue';

export default {
  name: "ChatHistory",

  components: { AudioPlayer },

  props: {
    displayID: {
      default: "",
      type: String
    },
    messages: {
      default: Array,
      type: Array
    },
    rooms: {
      default: Object,
      type: Object,
    },
    addSuggestion: {
      default: false,
      type: Boolean
    },
    loading: {
      default: false,
      type: Boolean
    },
    isShowdown: {
      default: false,
      type: Boolean
    },
    showdownFilters: {
      default: null,
      type: Object
    }
  },

  data() {
    return {
      suggestionTypes: [],
      allSuggestionTypes: [],
      askAdd: false,

      suggestionGameTypes: [
        'ofc',
        'nlh',
        'plo',
        'plo5c',
        'plo6c',
      ],

      suggestionData: {
        type: "thank",
        isTrigger: false,
        text: "",
        gameType: ["ofc", "nlh", "plo", "plo5c", "plo6c"],
      },

      ourAccountById: {}
    }
  },

  computed: {
    groupedMessages() {
      let messages = [...this.messages];

      let result = [];
      let last = null;
      let hasMarker = false;

      for (let message of messages) {
        if (message.marker) {
          hasMarker = true;
        }

        if (result.length == 0 || message.marker) {
          last = {...message};
          last.lines = [{
            '_id': last._id,
            'text': last.text,
            'name_mentions': last.name_mentions
          }];
          result.push(last);
        } else if (message.type === "handend") {
          if ((last && last.type === 'handend' && message.group === false) || (last && last.group === false)) {
            last = {...message};
            result.push(last);
          } else if (last && last.type === 'handend' && message.hand_num > last.hand_num) {
            last.end = message.hand_num;
            last.text = message.text;
          } else if (last && last.type === 'handend' && message.hand_num == last.hand_num) {
            continue;
          } else {
            last = {...message};
            last.end = last.hand_num;
            result.push(last);
          }
        } else if (message.type === "warning") {
          last = {...message};
          result.push(last);
        } else if (message.type === "game_event") {
          last = {...message};
          result.push(last);
        } else if (message.type === "suggestion") {
          last = {...message};
          result.push(last);
        } else if (["voice_chat", "emoji_chat", "gift_chat", "announcement_chat"].includes(message.type)) {
          last = {...message};
          result.push(last);
        } else {
          if (
            last && last.name === message.name
              && last.result === message.result
              && this.getTime(last.ts) === this.getTime(message.ts)
              && !['voice_chat', 'gift_chat', 'emoji_chat', 'announcement_chat'].includes(last.type)
          ) {
            last.lines.push({
              '_id': message._id,
              'text': message.text,
              'name_mentions': message.name_mentions
            });
          } else {
            last = {...message};
            last.lines = [{
              '_id': last._id,
              'text': last.text,
              'name_mentions': last.name_mentions
            }];
            result.push(last);
          }
        }
        // console.log(last);
      }

      if (!hasMarker) {
        if (!messages) {
          result.unshift({
            type: "marker"
          })
        } else {
          result.push({
            type: "marker"
          })
        }
      }
      return result;
    },

    clubmgmAccount() {
      return this.clubmgmAccountByName(this.displayID);
    },

    club() {
      return (this.clubmgmAccount || {}).club || {};
    },

    app() {
      if (!this.club) return 'Pkr2';
      if (!this.club.app) return 'Pkr2';
      return this.club.app;
    },

    ...mapGetters([
      'clubmgmAccountByName',
    ])
  },

  watch: {
    messages() {

      this.$emit("onMessages", this.$refs.messages);
    }
  },

  mounted() {
    this.loadSuggestionTypes();
    this.loadAccountSuggestionData();

    this.loadAllOur();
  },

  methods: {
    loadPastHand(msg) {
      this.$emit("loadHand", msg);
    },

    add(text) {
      this.askAdd = true;

      this.suggestionData.text = text;

      this.suggestionTypes = this.suggestionTypes.filter(el => el.value !== "new_type");

      // ! if is showdown, then load showdown type
      if (this.isShowdown) {
        let _filteredTypes = this.allSuggestionTypes.filter(el => {
          if (!el.filters) return false;
          for (let f in this.showdownFilters) {
            if (el.filters[f] !== this.showdownFilters[f]) return false;
          }
          return true;
        });

        if (!_filteredTypes.length) {
          this.suggestionTypes.push(Object.assign({}, {
            text: "New Type",
            description: "This option will add a new type",
            value: "new_type"
          }, this.showdownFilters));
          this.suggestionData.type = "new_type";
        } else {
          this.suggestionData.type = _filteredTypes[0].type;
        }
      }
    },

    addAction() {
      // save this data for next time
      this.saveAccountSuggestionData();

      let payload = {
        type: this.suggestionData.type,
        game_type: this.suggestionData.gameType,
        unverified: true
      };

      let url = "/api/suggestions/triggers";

      if (!this.suggestionData.isTrigger) {
        url = "/api/suggestions";
        payload.suggestion = this.suggestionData.text;
        if (this.isShowdown) {
          payload.filters = this.showdownFilters;
        }
      } else {
        payload.trigger = this.suggestionData.text;
      }

      this.$axios.post(url, payload).then(() => {
        // don't re-set suggestion data, keep it for next one
        this.$emit("action");

        if (this.isShowdown) {
          this.loadSuggestionTypes();
        }
      }).catch((err) => {
        console.error(err);
      })
    },

    loadAllOur() {
      this.$axios.get("/api/accounts_all").then((res) => {
        if (res && res.data && res.data.accounts) {
          res.data.accounts.forEach(el => {
            if (!el.pkr2_userId) return;
            this.ourAccountById[el.pkr2_userId] = el;
          })
        }
      })
    },

    loadAccountSuggestionData() {
      this.$axios.get("/api/account-settings").then((res) => {
        console.log("account settings", res);
        if (res && res.data && res.data.account_settings && res.data.account_settings.suggestion_data) {
          console.log("updated suggestion data!");
          this.suggestionData = res.data.account_settings.suggestion_data
        }
      }).catch((err) => {
        console.error(err);
      })
    },

    saveAccountSuggestionData() {
      this.$axios.post("/api/account-settings", {'account_settings': {'suggestion_data': this.suggestionData}}).then(() => {
        console.log("updated account settings!");
      }).catch((err) => {
        console.error(err);
      })
    },

    loadSuggestionTypes() {
      this.$axios.get("/api/suggestions/types").then((res) => {
        const data = res.data;

        for (let type of data.types) {
          this.allSuggestionTypes.push(type);
          this.suggestionTypes.push({
            text: type.type.charAt(0).toUpperCase() + type.type.slice(1),
            description: type.description,
            value: type.type
          })
        }
      }).catch((err) => {
        console.error(err);
      })
    },

    scrollToMarker() {
      if (this.$refs.marker && this.$refs.marker[0]) {
        // scroll to center
        try {
          // scrollIntoViewIfNeeded is not implemented on some browsers, firefox specifically don't implement it
          // it would more or less make the site unsusable on firefox
          this.$refs.marker[0].scrollIntoView({block: 'center'});
          // adjust offset
          this.$refs.messages.scrollTop = this.$refs.marker[0].offsetTop - this.$refs.messages.clientHeight / 10;
        } catch {
          // pass
        }
      }
    },

    hasPastNames() {
      let nicknames = (this.clubmgmAccount || {}).nicknames;
      return nicknames && nicknames.length > 0
    },

    getParsedHandendText(text) {
      if (this.club.currency === "USD") return text;

      let newText = text.replace("($", "(").replace(")", ` ${this.club.currency ? this.club.currency : ''})`);

      return newText;
    },

    onScroll(evt) {
      let tgt = evt.target;
      this.$emit("onScroll", tgt);
    },

    isOurs(name) {
      return !!this.clubmgmAccountByName(name);
    },
    isOursId(pkr2Id) {
      if (!this.ourAccountById) return false;
      if (!(pkr2Id in this.ourAccountById)) return false;
      return true;
    },

    getHandendClass(text) {
      if (text.includes("won")) {
        if (text.includes("huge")) {
          return "huge";
        } else if (text.includes("large")) {
          return "large";
        }
      } else if (text.includes("lost")) {
        if (text.includes("with AA")) {
          return "with-aa";
        } else if (text.includes("with QQ")) {
          return "with-qq";
        } else if (text.includes("with KK")) {
          return "with-kk";
        } else if (text.includes("set over set")) {
          return "set-over-set";
        } else if (text.includes("got unlucky")) {
          return "unlucky"
        }
      }
    },

    getTime(ts) {
      var time = ts.toLocaleTimeString();
      var parts = time.split(" ")[0].split(':');
      var date = ts.toLocaleString();

      var ampm = time.split(" ")[1]
      if (ampm === "PM" && parts[0] >= 1 && parts[0] <= 11) {
        parts[0] = parseInt(parts[0]) + 12;
      }

      var partsDate = date.split("/")

      return partsDate[0] + "/" + partsDate[1] + " " + parts[0] + ":" + parts[1];
    },

    openPopup() {
      window.open(
          `/questions?accountId=${this.clubmgmAccount._id}`,
          "",
          "width=400,height=600,left=200,top=200"
      );
    },

    formatNumber(v, _d=2) {
      if (!v) return v;
      return (v).toFixed(_d).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    },



    isSenderActiveAccount(msg) {
      if (msg.senderType === 'bot_current') return true;
      return msg.name === this.displayID;
    },
    isSenderOtherBot(msg) {
      if (msg.senderType === 'bot_other') return true;
      return this.isOursId(msg.senderId);
    },
    isSenderManager(msg) {
      if (msg.senderType === 'manager') return true;

      if (!this.club) return false;
      if (!this.club.lobby_info) return false;
      if (!this.club.lobby_info.managers) return false;

      return this.club.lobby_info.managers.includes(msg.senderId);
    },
    isSenderOwner(msg) {
      if (msg.senderType === 'owner') return true;

      if (!this.club) return false;
      if (!this.club.lobby_info) return false;
      if (!this.club.lobby_info.managers) return false;

      return this.club.lobby_info.ownerId === msg.senderId;
    },

    getSenderTitleStyle(msg) {
      if (this.isSenderManager(msg)) {
        return "color: #DF80EC";
      }

      if (this.isSenderOwner(msg)) {
        return "color: #EC896A";
      }

      return '';
    },


    getEmojiImage(emojiId) {
      return `emoji_${this.app}_${emojiId}`;
    },
  }
}
</script>

<style scoped>

.btn-add:hover {
  background-color: #424242;
}

.messages-container {
  /*height: calc(100vh - 96px) !important;*/
  /*max-height: calc(100vh - 96px) !important;*/
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
}

.messages {
  position: relative;
  height: 100% !important;
  /*max-height: 80vh;*/
  overflow-y: auto;
}
.text {
  background-color: #262626;
}

.v-text-field--filled {
  border-radius: 4px !important;
}

.v-text-field--filled >>> .v-input__slot::before {
  border-style: none !important;
}

.v-card {
  /*height: 90vh !important;*/
  /*max-height: 90vh;*/
  overflow-y: auto;
}
.v-divider {
  background-color: darkred;
}

.chat-room {
  background-color: #8c47d5;
  width: 100%;
  height: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chat-room > span {
  background-color: #8c47d5;
  border-radius: 4px;
  padding: 4px 12px;
  font-weight: 900;
}

.chat-suggestion {
  background-color: #176f95;
  width: 100%;
  height: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chat-suggestion > span {
  background-color: #176f95;
  border-radius: 4px;
  padding: 4px 12px;
  font-weight: 900;
}
#marker {
  background-color: red;
  width: 100%;
  height: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  margin-top: 30px;
}
#marker > span {
  background-color: red;
  /*border: 3px solid red;*/
  border-radius: 4px;
  padding: 2px 8px;
}

.messages-list p:last-of-type {
  margin-bottom: 0;
}

.huge {
  color: #eec43e;
  font-weight: 900;
}

.large {
  color: #80f645;
  font-weight: 500;
}

.with-aa, .with-qq, .with-kk, .unlucky {
  color: #e54654;
  font-weight: 500;
}

.set-over-set {
  color: #e54654;
  font-weight: 900;
}

.handend-div p {
  margin-bottom: 0;
}

.handend-div p:last-of-type {
  margin-bottom: 16px;
}

.mention-highlight {
  background-color: #4e013d;
  color: #ff6a1e;

  font-weight: bold;

  padding: 2px 6px;
  border-radius: 5px;
}
</style>
